import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, getDocs, query, where, doc, getDoc, writeBatch, arrayUnion, onSnapshot, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getProjectMemberPath, getUserProjectPath } from '../../utils/collectionPaths';

// MUI Imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

function InviteHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accountMismatch, setAccountMismatch] = useState(null);

  const normalizeEmail = (value) => (value || '').trim().toLowerCase();

  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setError('Invalid invitation link. No token provided.');
        setLoading(false);
        return;
      }

      try {
        // Query invitations collection for the token
        // IMPORTANT: Must include status == 'pending' filter for security rules
        const invitationsRef = collection(db, 'invitations');
        const q = query(
          invitationsRef,
          where('token', '==', token),
          where('status', '==', 'pending')  // Required by security rules
        );
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          setError('Invalid invitation token. This invitation link is not valid.');
          setLoading(false);
          return;
        }

        const invitationDoc = querySnapshot.docs[0];
        const invitationData = invitationDoc.data();
        
        // Check if invitation has expired
        const now = new Date();
        const expiresAt = invitationData.expiresAt.toDate();
        
        if (now > expiresAt) {
          setError('This invitation has expired. Please contact the project owner for a new invitation.');
          setLoading(false);
          return;
        }

        // Check if invitation has already been accepted
        if (invitationData.status === 'accepted') {
          setError('This invitation has already been accepted.');
          setLoading(false);
          return;
        }

        setInvitation({ ...invitationData, id: invitationDoc.id });
        setLoading(false);
      } catch (err) {
        console.error('Error validating invitation token:', err);
        setError('Failed to validate invitation. Please try again.');
        setLoading(false);
      }
    };

    // Check if user is logged in and auto-accept invitation
    const handleInvitationAccess = async () => {
      // First validate the token
      await validateToken();
    };

    handleInvitationAccess();
  }, [searchParams]);

  // Accept invitation for already logged-in user
  const acceptInvitationForLoggedInUser = async () => {
    if (!currentUser || !invitation || accepting) return;

    setAccepting(true);
    setError('');
    setAccountMismatch(null);

    try {
      const currentEmail = normalizeEmail(currentUser.email);
      const recipientEmail = normalizeEmail(invitation.recipientEmail);

      if (!recipientEmail || currentEmail !== recipientEmail) {
        setAccountMismatch({
          currentEmail: currentUser.email || 'unknown account',
          recipientEmail: invitation.recipientEmail || 'the invited email',
        });
        setError(
          `This invitation is for ${invitation.recipientEmail || 'another email address'}. ` +
          `You are signed in as ${currentUser.email || 'another account'}.`
        );
        setAccepting(false);
        return;
      }

      // Get project name first
      const projectRef = doc(db, 'projects', invitation.projectId);
      const projectSnap = await getDoc(projectRef);

      if (!projectSnap.exists()) {
        setError('Project not found. Please contact the project owner.');
        setAccepting(false);
        return;
      }

      const projectName = projectSnap.data().projectName || 'Unknown Project';

      // Prepare member data
      const memberData = {
        role: invitation.roleToAssign,
        email: currentUser.email,
        joinedAt: new Date()
      };

      // Prepare user project data
      const userProjectData = {
        name: projectName,
        role: invitation.roleToAssign,
        addedAt: new Date(),
        updatedAt: new Date()
      };

      // Step 1: Batch write for member, userProject, and invitation status
      // Note: memberIds update is done separately (Step 2) because Firestore
      // security rules evaluate each operation against pre-batch state.
      // The project update rule requires isProjectMember(), but the member doc
      // hasn't been committed yet during batch evaluation — causing the entire
      // batch to fail with permission-denied.
      const batch = writeBatch(db);

      const memberRef = doc(db, getProjectMemberPath(invitation.projectId, currentUser.uid));
      const userProjectRef = doc(db, getUserProjectPath(currentUser.uid, invitation.projectId));
      const invitationRef = doc(db, 'invitations', invitation.id);

      batch.set(memberRef, memberData);
      batch.set(userProjectRef, userProjectData);
      batch.update(invitationRef, {
        status: 'accepted',
        acceptedAt: new Date(),
        acceptedBy: currentUser.uid
      });

      await batch.commit();

      // Step 2: Add user to memberIds array separately
      // Now the member doc exists, so isProjectMember() passes
      await updateDoc(projectRef, {
        memberIds: arrayUnion(currentUser.uid)
      });

      setSuccess(`Invitation accepted! Updating your project list...`);

      // CRITICAL FIX: Wait for Firestore listener to propagate updates
      // Create a snapshot listener that resolves when project appears in user's list
      const waitForProjectUpdate = new Promise((resolve) => {
        const projectsQuery = query(
          collection(db, 'projects'),
          where('memberIds', 'array-contains', currentUser.uid)
        );

        const unsubscribe = onSnapshot(projectsQuery, (snapshot) => {
          // Check if the invited project is now in the user's project list
          const hasProject = snapshot.docs.some(doc => doc.id === invitation.projectId);

          if (hasProject) {
            console.log('✓ Project found in user list, safe to navigate');
            unsubscribe(); // Clean up listener
            resolve();
          }
        }, (error) => {
          console.error('Error waiting for project update:', error);
          unsubscribe();
          resolve(); // Continue anyway after error
        });

        // Safety timeout: proceed after 5 seconds even if listener doesn't fire
        setTimeout(() => {
          console.warn('⚠ Timeout waiting for project update, navigating anyway');
          unsubscribe();
          resolve();
        }, 5000);
      });

      // Wait for state to propagate
      await waitForProjectUpdate;

      setSuccess(`Invitation accepted! You've been added to "${projectName}". Redirecting...`);

      // Now it's safe to navigate - state is guaranteed to be updated
      setTimeout(() => {
        navigate('/all-projects');
      }, 1000);

    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('Failed to accept invitation. Please try again or contact support.');
      setAccepting(false);
    }
  };

  // Auto-accept invitation if user is logged in
  useEffect(() => {
    if (invitation && currentUser && !accepting && !error && !success) {
      acceptInvitationForLoggedInUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitation, currentUser]);

  const handleLogin = () => {
    // Store invitation data in sessionStorage for the login process
    sessionStorage.setItem('pendingInvitation', JSON.stringify(invitation));
    navigate('/login');
  };

  const handleSwitchAccount = async () => {
    if (invitation) {
      sessionStorage.setItem('pendingInvitation', JSON.stringify(invitation));
    }
    await signOut(auth);
    navigate('/login');
  };

  const handleGoToDashboard = () => {
    navigate('/all-projects');
  };

  if (loading || accepting) {
    return (
      <Container maxWidth="sm">
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2
        }}>
          <CircularProgress />
          <Typography variant="body1">
            {loading ? 'Validating invitation...' : 'Accepting invitation...'}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2
        }}>
          <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" component="h1" gutterBottom>
              Success!
            </Typography>
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          gap: 3
        }}>
          <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
            <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" component="h1" gutterBottom>
              Invitation Error
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={handleGoToDashboard}>
              Go to Main Page
            </Button>
            {accountMismatch && (
              <Button variant="outlined" onClick={handleSwitchAccount} sx={{ mt: 2, ml: 1 }}>
                Sign in as {accountMismatch.recipientEmail}
              </Button>
            )}
          </Paper>
        </Box>
      </Container>
    );
  }

  // Show login option if user is not logged in
  if (!currentUser) {
    return (
      <Container maxWidth="sm">
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 3
        }}>
          <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Project Invitation
            </Typography>

            <Typography variant="body1" sx={{ mb: 3 }}>
              You&apos;ve been invited to join a project as a <strong>{invitation?.roleToAssign}</strong>.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please log in to accept this invitation.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={handleLogin}
                sx={{ mb: 1 }}
              >
                Log In
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // If user is logged in, the useEffect will auto-accept
  // This is just a fallback in case something goes wrong
  return (
    <Container maxWidth="sm">
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1">Processing invitation...</Typography>
      </Box>
    </Container>
  );
}

export default InviteHandler; 
